// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, addDoc, onSnapshot, query, orderBy, limit, doc, updateDoc, writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { PostGalleryModal, ShopKeyPromptModal, TransformedPost } from '../components/scraper';
import { OnboardSubmitForm, QueueList, ImportProgressModal, OnboardRecord } from '../components/onboard';

// ── OnboardPage — thin orchestrator ──────────────────────────────────────

const OnboardPage: React.FC = () => {
  const [records, setRecords] = useState<OnboardRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Active flow state
  const [activeRecord, setActiveRecord] = useState<OnboardRecord | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState<TransformedPost[]>([]);
  const [shopFlowOpen, setShopFlowOpen] = useState(false);

  // Import progress
  const [importProgress, setImportProgress] = useState<{
    open: boolean;
    step: 'linking' | 'importing' | 'done' | 'error';
    shopId?: string;
    error?: string;
  }>({ open: false, step: 'linking' });

  // ── Data: listen to onboardQueue ─────────────────────────────────────

  useEffect(() => {
    const q = query(
      collection(db, 'onboardQueue'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, snap => {
      const items: OnboardRecord[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as OnboardRecord));
      setRecords(items);
      setLoading(false);
    }, () => {
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (username: string) => {
    await addDoc(collection(db, 'onboardQueue'), {
      username,
      instagramUrl: `https://www.instagram.com/${username}/`,
      status: 'pending',
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || 'anonymous',
      posts: [],
      postCount: 0,
    });
  }, []);

  const handleTapRecord = useCallback((rec: OnboardRecord) => {
    if (rec.status === 'ready' && rec.posts && rec.posts.length > 0) {
      setActiveRecord(rec);
      setGalleryOpen(true);
    }
  }, []);

  const handleImportSelected = useCallback((_username: string, posts: TransformedPost[]) => {
    setSelectedPosts(posts);
    setGalleryOpen(false);
    setShopFlowOpen(true);
  }, []);

  const handleCloseGallery = useCallback(() => {
    setGalleryOpen(false);
    setActiveRecord(null);
  }, []);

  const handleCancelShopFlow = useCallback(() => {
    setShopFlowOpen(false);
    setActiveRecord(null);
    setSelectedPosts([]);
  }, []);

  const handleShopLinked = useCallback(async (shopId: string) => {
    if (!activeRecord || selectedPosts.length === 0) return;
    setShopFlowOpen(false);

    // Show progress modal
    setImportProgress({ open: true, step: 'linking', shopId });

    try {
      // Mark importing
      await updateDoc(doc(db, 'onboardQueue', activeRecord.id), {
        status: 'importing',
        shopId,
      });

      setImportProgress({ open: true, step: 'importing', shopId });

      // Write posts in batches (Firestore batch limit is 500)
      const BATCH_SIZE = 450;
      for (let i = 0; i < selectedPosts.length; i += BATCH_SIZE) {
        const chunk = selectedPosts.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        const igPostsRef = collection(db, 'shops', shopId, 'IGPosts');

        for (const post of chunk) {
          const postDoc = doc(igPostsRef);
          batch.set(postDoc, {
            ...post,
            status: 'approved',
            createdAt: serverTimestamp(),
            importedFrom: 'onboard',
            onboardRecordId: activeRecord.id,
          });
        }
        await batch.commit();
      }

      // Mark done
      await updateDoc(doc(db, 'onboardQueue', activeRecord.id), {
        status: 'done',
        shopId,
      });

      setImportProgress({ open: true, step: 'done', shopId });

      // Auto-close after 2s
      setTimeout(() => {
        setImportProgress({ open: false, step: 'linking' });
        setActiveRecord(null);
        setSelectedPosts([]);
      }, 2000);

    } catch (err: any) {
      console.error('Failed to import posts:', err);
      const errorMsg = err?.message || 'Import failed';

      setImportProgress({ open: true, step: 'error', shopId, error: errorMsg });

      await updateDoc(doc(db, 'onboardQueue', activeRecord.id), {
        status: 'error',
        error: errorMsg,
      }).catch(() => { });

      setTimeout(() => {
        setImportProgress({ open: false, step: 'linking' });
        setActiveRecord(null);
        setSelectedPosts([]);
      }, 4000);
    }
  }, [activeRecord, selectedPosts]);

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Indeterminate progress animation */}
      <style>{`
        @keyframes progress-indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(200%); }
        }
        .animate-progress-indeterminate {
          width: 40%;
          animation: progress-indeterminate 1.8s ease-in-out infinite;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Onboard Store</h1>
            <p className="text-sm text-gray-500 mt-0.5">Queue an Instagram store for import</p>
          </div>
          {records.filter(r => r.status === 'fetching').length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-full border border-blue-200">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-[10px] font-bold text-blue-700">Scraping</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-5">
        <OnboardSubmitForm onSubmit={handleSubmit} />
        <QueueList
          records={records}
          loading={loading}
          onTapRecord={handleTapRecord}
        />
      </div>

      {/* Post Gallery Modal */}
      {activeRecord && (
        <PostGalleryModal
          isOpen={galleryOpen}
          username={activeRecord.username}
          posts={activeRecord.posts || []}
          onClose={handleCloseGallery}
          onImportSelected={handleImportSelected}
        />
      )}

      {/* Shop Link/Create Modal — same component as scraper page, in callback mode */}
      <ShopKeyPromptModal
        isOpen={shopFlowOpen}
        username={activeRecord?.username || ''}
        onShopLinked={handleShopLinked}
        onCancel={handleCancelShopFlow}
      />

      {/* Import Progress Modal */}
      <ImportProgressModal
        isOpen={importProgress.open}
        username={activeRecord?.username || ''}
        totalPosts={selectedPosts.length}
        step={importProgress.step}
        shopId={importProgress.shopId}
        error={importProgress.error}
      />
    </div>
  );
};

export default OnboardPage;
